import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, LayoutDashboard, Users, ExternalLink, Image as ImageIcon, Truck, MapPin, Tags, UtensilsCrossed, ClipboardList, Package, ShoppingCart, ArrowLeftRight, BarChart2, FileText, UserCircle, TrendingUp, Clock, ChevronDown, Columns, Eye, EyeOff, LogIn, Upload, FileSpreadsheet, X as CloseIcon, Calculator, Calendar, Settings } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
// Sub-views
import { DashboardView } from '../components/roi/DashboardView';
import { DailyOpsView } from '../components/roi/DailyOpsView';
import { InventoryView } from '../components/roi/InventoryView';
import { PnLView } from '../components/roi/PnLView';
import { FoodCostView } from '../components/roi/FoodCostView';
import { PayrollView } from '../components/roi/PayrollView';
import { VendorView } from '../components/roi/VendorView';
import { ReportingView } from '../components/roi/ReportingView';
import { InventoryMgmtView } from '../components/roi/InventoryMgmtView';
import { ExternalLinksView } from '../components/roi/ExternalLinksView';
import { GalleryView } from '../components/roi/GalleryView';
interface ROIAppProps {
  onBack?: () => void;
}
type DashboardPage = 'dashboard' | 'daily-ops' | 'inventory' | 'pnl' | 'food-cost' | 'payroll' | 'vendors' | 'reports' | 'inventory-mgmt' | 'external-links' | 'gallery';
export function ROIApp({
  onBack
}: ROIAppProps) {
  const screenInit = useScreenInit();
  const [view, setView] = useState<'login' | 'dashboard' | 'register'>(screenInit?.view || 'login');
  const [activePage, setActivePage] = useState<DashboardPage>(screenInit?.activePage || 'dashboard');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  if (view === 'register') {
    return <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center p-4 font-sans" data-id="element-3522">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-[1000px] bg-white rounded-[32px] shadow-2xl overflow-hidden flex flex-col md:flex-row min-h-[650px]" data-id="element-3523">
          {/* Left Panel - Form */}
          <div className="flex-1 p-10 flex flex-col justify-center" data-id="element-3524">
            <div className="max-w-sm mx-auto w-full" data-id="element-3525">
              <button onClick={() => setView('login')} className="flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-slate-600 mb-8 transition-colors" data-id="element-3526">
                ← Back to Login
              </button>

              <h3 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-3527">
                Create Account
              </h3>
              <p className="text-slate-500 text-sm mb-8" data-id="element-3528">
                Join The Culture Gourmet community
              </p>

              <button className="w-full py-3 px-4 border border-slate-200 rounded-xl flex items-center justify-center gap-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors mb-6" data-id="element-3529">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" data-id="element-3530" />
                Sign up with Google
              </button>

              <div className="relative flex items-center justify-center mb-6" data-id="element-3531">
                <div className="absolute inset-0 flex items-center" data-id="element-3532">
                  <div className="w-full border-t border-slate-100" data-id="element-3533"></div>
                </div>
                <span className="relative px-4 bg-white text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-3534">
                  Or register with email
                </span>
              </div>

              <div className="space-y-4 mb-8" data-id="element-3535">
                <InputGroup label="Full Name" icon={UserCircle} placeholder="John Doe" data-id="element-3536" />
                <InputGroup label="Email Address" icon={FileText} placeholder="you@example.com" type="email" data-id="element-3537" />
                <PasswordInput label="Password" show={showPassword} onToggle={() => setShowPassword(!showPassword)} data-id="element-3538" />
                <PasswordInput label="Confirm Password" show={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} data-id="element-3539" />
              </div>

              <button onClick={() => setView('dashboard')} className="w-full py-4 bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white rounded-xl font-bold text-sm shadow-xl shadow-blue-500/20 hover:opacity-90 transition-all flex items-center justify-center gap-2" data-id="element-3540">
                Create Account →
              </button>

              <p className="text-center mt-6 text-sm text-slate-500" data-id="element-3541">
                Already have an account?{' '}
                <button onClick={() => setView('login')} className="font-bold text-[#10B981] hover:text-[#059669]" data-id="element-3542">
                  Sign In →
                </button>
              </p>
            </div>
          </div>

          {/* Right Panel - Branding */}
          <div className="w-full md:w-[45%] bg-[#0F172A] p-12 flex flex-col items-center justify-center text-center relative overflow-hidden text-white" data-id="element-3543">
            <div className="relative z-10 w-full max-w-xs" data-id="element-3544">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 rounded-full text-[10px] font-bold uppercase tracking-widest mb-10" data-id="element-3545">
                ✨ Join the community
              </div>
              <div className="w-24 h-24 bg-white rounded-2xl p-3 mb-8 mx-auto shadow-2xl" data-id="element-3546">
                <img src="https://cdn.magicpatterns.com/uploads/a1zmqJkTyzi54U3fHLHet2/image.png" alt="Logo" className="w-full h-full object-contain" data-id="element-3547" />
              </div>
              <h2 className="text-4xl font-bold mb-2" data-id="element-3548">Start Your</h2>
              <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#3B82F6] mb-6" data-id="element-3549">
                Journey
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-12" data-id="element-3550">
                Create your account and unlock the full potential of The Culture
                Gourmet platform.
              </p>
              <div className="space-y-6 text-left" data-id="element-3551">
                <Step step={1} title="Create Account" desc="Fill in your details" active data-id="element-3552" />
                <Step step={2} title="Verify Email" desc="Confirm your email" data-id="element-3553" />
                <Step step={3} title="Get Approved" desc="Admin reviews your request" data-id="element-3554" />
              </div>
            </div>
            <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none" data-id="element-3555">
              <div className="absolute top-[-10%] right-[-10%] w-64 h-64 bg-[#10B981] rounded-full blur-[100px]" data-id="element-3556" />
              <div className="absolute bottom-[-10%] left-[-10%] w-64 h-64 bg-[#3B82F6] rounded-full blur-[100px]" data-id="element-3557" />
            </div>
          </div>
        </motion.div>
      </div>;
  }
  if (view === 'login') {
    return <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center p-4 font-sans" data-id="element-3558">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-[1000px] bg-white rounded-[32px] shadow-2xl overflow-hidden flex flex-col md:flex-row min-h-[600px]" data-id="element-3559">
          {/* Left Panel */}
          <div className="w-full md:w-[45%] bg-[#1A233A] p-12 flex flex-col items-center justify-center text-center relative overflow-hidden" data-id="element-3560">
            <div className="relative z-10" data-id="element-3561">
              <div className="w-32 h-32 bg-white rounded-3xl p-4 mb-8 mx-auto shadow-2xl" data-id="element-3562">
                <img src="https://cdn.magicpatterns.com/uploads/a1zmqJkTyzi54U3fHLHet2/image.png" alt="Logo" className="w-full h-full object-contain" data-id="element-3563" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-2" data-id="element-3564">
                The Culture
              </h1>
              <h2 className="text-4xl font-bold text-[#A78BFA] mb-6" data-id="element-3565">
                Gourmet
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed max-w-[280px] mx-auto" data-id="element-3566">
                Where culinary artistry meets cultural excellence. Manage your
                gourmet empire with style.
              </p>
            </div>
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" data-id="element-3567">
              <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-purple-500 rounded-full blur-[100px]" data-id="element-3568" />
              <div className="absolute bottom-[-10%] right-[-10%] w-64 h-64 bg-blue-500 rounded-full blur-[100px]" data-id="element-3569" />
            </div>
          </div>

          {/* Right Panel */}
          <div className="flex-1 p-12 flex flex-col justify-center" data-id="element-3570">
            <div className="max-w-sm mx-auto w-full" data-id="element-3571">
              <h3 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-3572">
                Welcome Back
              </h3>
              <p className="text-slate-500 text-sm mb-8" data-id="element-3573">
                Sign in to access your dashboard
              </p>

              <button className="w-full py-3 px-4 border border-slate-200 rounded-xl flex items-center justify-center gap-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors mb-6" data-id="element-3574">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" data-id="element-3575" />
                Continue with Google
              </button>

              <div className="relative flex items-center justify-center mb-6" data-id="element-3576">
                <div className="absolute inset-0 flex items-center" data-id="element-3577">
                  <div className="w-full border-t border-slate-100" data-id="element-3578"></div>
                </div>
                <span className="relative px-4 bg-white text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-3579">
                  Or sign in with email
                </span>
              </div>

              <div className="space-y-4 mb-8" data-id="element-3580">
                <InputGroup label="Email Address" icon={FileText} defaultValue="aliyu@codelabprojects.com" data-id="element-3581" />
                <PasswordInput label="Password" show={showPassword} onToggle={() => setShowPassword(!showPassword)} defaultValue="••••••••••••" data-id="element-3582" />
              </div>

              <button onClick={() => setView('dashboard')} className="w-full py-4 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-xl font-bold text-sm shadow-xl shadow-purple-500/20 hover:opacity-90 transition-all flex items-center justify-center gap-2" data-id="element-3583">
                Sign In <LogIn size={18} data-id="element-3584" />
              </button>

              <p className="text-center mt-8 text-sm text-slate-500" data-id="element-3585">
                Don't have an account?{' '}
                <button onClick={() => setView('register')} className="font-bold text-purple-600 hover:text-purple-700" data-id="element-3586">
                  Create Account →
                </button>
              </p>
            </div>
          </div>
        </motion.div>
      </div>;
  }
  return <div className="flex h-screen bg-white font-sans relative overflow-hidden" data-id="element-3587">
      {/* Upload Modal Overlay - Commented out per user request */}
      {/*
          <AnimatePresence>
            {showUploadModal && (
              <motion.div
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
                className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
              >
                <motion.div
                  initial={{
                    scale: 0.9,
                    opacity: 0,
                    y: 20,
                  }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    scale: 0.9,
                    opacity: 0,
                    y: 20,
                  }}
                  className="w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
                >
                  <div className="p-8">
                    <div className="flex justify-between items-start mb-6">
                      <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600">
                        <FileSpreadsheet size={24} />
                      </div>
                      <button
                        onClick={() => setShowUploadModal(false)}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400"
                      >
                        <CloseIcon size={20} />
                      </button>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      Upload Financial Records
                    </h3>
                    <p className="text-slate-500 text-sm mb-8">
                      To provide accurate insights, please upload your latest
                      financial records. Files must be in{' '}
                      <span className="font-bold text-slate-900">
                        Excel (.xlsx)
                      </span>{' '}
                      format.
                    </p>
                    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100/50 transition-colors cursor-pointer group mb-8">
                      <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center text-slate-400 group-hover:text-emerald-500 transition-colors mb-4">
                        <Upload size={20} />
                      </div>
                      <p className="text-sm font-bold text-slate-900 mb-1">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-slate-400">
                        Excel files only (max. 10MB)
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowUploadModal(false)}
                        className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-sm font-bold transition-all"
                      >
                        Skip for now
                      </button>
                      <button
                        onClick={() => setShowUploadModal(false)}
                        className="flex-1 py-3 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-emerald-500/20 transition-all"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          */}

      {/* Sidebar */}
      <div className="w-64 border-r border-slate-100 flex flex-col h-full shrink-0 bg-white" data-id="element-3588">
        <div className="p-6 flex items-center gap-3 border-b border-slate-50" data-id="element-3589">
          <div className="w-8 h-8 rounded-lg overflow-hidden" data-id="element-3590">
            <img src="https://cdn.magicpatterns.com/uploads/a1zmqJkTyzi54U3fHLHet2/image.png" alt="Logo" className="w-full h-full object-contain" data-id="element-3591" />
          </div>
          <span className="font-bold text-slate-900" data-id="element-3592">The Culture Gourmet</span>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-8" data-id="element-3593">
          {/* Search */}
          <div className="relative" data-id="element-3594">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3595">
              <Search size={14} className="text-slate-400" data-id="element-3596" />
            </div>
            <input type="text" placeholder="Search" className="w-full pl-9 pr-10 py-2 bg-slate-50 border-none rounded-lg text-xs focus:ring-0" data-id="element-3597" />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-3598">
              <span className="text-[10px] font-bold text-slate-400" data-id="element-3599">
                Ctrl K
              </span>
            </div>
          </div>

          {/* Main Navigation */}
          <div data-id="element-3600">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-3" data-id="element-3601">
              Main Menu
            </p>
            <div className="space-y-1" data-id="element-3602">
              <SidebarItem icon={LayoutDashboard} label="Dashboard" active={activePage === 'dashboard'} onClick={() => setActivePage('dashboard')} data-id="element-3603" />
              <SidebarItem icon={Calendar} label="Daily Operations" active={activePage === 'daily-ops'} onClick={() => setActivePage('daily-ops')} data-id="element-3604" />
              <SidebarItem icon={Package} label="Inventory & Purchasing" active={activePage === 'inventory'} onClick={() => setActivePage('inventory')} data-id="element-3605" />
              <SidebarItem icon={Calculator} label="P&L Recap" active={activePage === 'pnl'} onClick={() => setActivePage('pnl')} data-id="element-3606" />
              <SidebarItem icon={UtensilsCrossed} label="Food Cost" active={activePage === 'food-cost'} onClick={() => setActivePage('food-cost')} data-id="element-3607" />
              <SidebarItem icon={Users} label="Payroll Management" active={activePage === 'payroll'} onClick={() => setActivePage('payroll')} data-id="element-3608" />
              <SidebarItem icon={Truck} label="Vendor Management" active={activePage === 'vendors'} onClick={() => setActivePage('vendors')} data-id="element-3609" />
              <SidebarItem icon={BarChart2} label="Reporting Suite" active={activePage === 'reports'} onClick={() => setActivePage('reports')} data-id="element-3610" />
            </div>
          </div>

          {/* Management */}
          <div data-id="element-3611">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-3" data-id="element-3612">
              Management
            </p>
            <div className="space-y-1" data-id="element-3613">
              <SidebarItem icon={Users} label="Users Management" data-id="element-3614" />
              <SidebarItem icon={Package} label="Inventory Mgmt System" active={activePage === 'inventory-mgmt'} onClick={() => setActivePage('inventory-mgmt')} data-id="element-3615" />
              <SidebarItem icon={ExternalLink} label="External Links" active={activePage === 'external-links'} onClick={() => setActivePage('external-links')} data-id="element-3616" />
              <SidebarItem icon={ImageIcon} label="Gallery" active={activePage === 'gallery'} onClick={() => setActivePage('gallery')} data-id="element-3617" />
              <SidebarItem icon={UtensilsCrossed} label="Recipes" data-id="element-3618" />
              <SidebarItem icon={ClipboardList} label="Master Prep List" data-id="element-3619" />
              <SidebarItem icon={Settings} label="Settings" data-id="element-3620" />
            </div>
          </div>
        </div>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-100" data-id="element-3621">
          <div className="flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer" data-id="element-3622">
            <div className="flex items-center gap-3" data-id="element-3623">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-3624">
                AJ
              </div>
              <div data-id="element-3625">
                <p className="text-xs font-bold text-slate-900" data-id="element-3626">Adeel Jabbar</p>
                <p className="text-[10px] text-slate-400" data-id="element-3627">
                  admin@aivisualpro.com
                </p>
              </div>
            </div>
            <ChevronDown size={14} className="text-slate-400" data-id="element-3628" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-[#F8FAFC]" data-id="element-3629">
        <header className="h-14 bg-white border-b border-slate-100 px-6 flex items-center justify-between" data-id="element-3630">
          <div className="flex items-center gap-4" data-id="element-3631">
            <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-all" data-id="element-3632">
              <Columns size={18} data-id="element-3633" />
            </button>
            <div className="h-4 w-[1px] bg-slate-200" data-id="element-3634" />
            <span className="text-sm font-semibold text-slate-900 capitalize" data-id="element-3635">
              {activePage.replace('-', ' ')}
            </span>
          </div>

          <div className="flex items-center gap-4" data-id="element-3636">
            {onBack && <button onClick={onBack} className="text-xs font-bold text-slate-400 hover:text-slate-600 uppercase tracking-widest" data-id="element-3637">
                ← Back to Admin
              </button>}
            <button onClick={() => setView('login')} className="px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 rounded-lg transition-all border border-slate-200" data-id="element-3638">
              Logout
            </button>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto" data-id="element-3639">
          <AnimatePresence mode="wait" data-id="element-3640">
            <motion.div key={activePage} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-3641">
              {activePage === 'dashboard' && <DashboardView data-id="element-3642" />}
              {activePage === 'daily-ops' && <DailyOpsView data-id="element-3643" />}
              {activePage === 'inventory' && <InventoryView data-id="element-3644" />}
              {activePage === 'pnl' && <PnLView data-id="element-3645" />}
              {activePage === 'food-cost' && <FoodCostView data-id="element-3646" />}
              {activePage === 'payroll' && <PayrollView data-id="element-3647" />}
              {activePage === 'vendors' && <VendorView data-id="element-3648" />}
              {activePage === 'reports' && <ReportingView data-id="element-3649" />}
              {activePage === 'inventory-mgmt' && <InventoryMgmtView data-id="element-3650" />}
              {activePage === 'external-links' && <ExternalLinksView data-id="element-3651" />}
              {activePage === 'gallery' && <GalleryView data-id="element-3652" />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>;
}
function SidebarItem({
  icon: Icon,
  label,
  active = false,
  onClick
}: any) {
  return <button onClick={onClick} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${active ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`} data-id="element-3653">
      <Icon size={16} data-id="element-3654" />
      <span className="text-xs font-semibold" data-id="element-3655">{label}</span>
    </button>;
}
function InputGroup({
  label,
  icon: Icon,
  placeholder,
  type = 'text',
  defaultValue
}: any) {
  return <div data-id="element-3656">
      <label className="block text-xs font-bold text-slate-700 mb-2" data-id="element-3657">
        {label}
      </label>
      <div className="relative" data-id="element-3658">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-3659">
          <Icon size={18} className="text-slate-400" data-id="element-3660" />
        </div>
        <input type={type} placeholder={placeholder} defaultValue={defaultValue} className="w-full pl-11 pr-4 py-3 bg-[#F8FAFC] border border-slate-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all" data-id="element-3661" />
      </div>
    </div>;
}
function PasswordInput({
  label,
  show,
  onToggle,
  defaultValue
}: any) {
  return <div data-id="element-3662">
      <label className="block text-xs font-bold text-slate-700 mb-2" data-id="element-3663">
        {label}
      </label>
      <div className="relative" data-id="element-3664">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-3665">
          <LogIn size={18} className="text-slate-400" data-id="element-3666" />
        </div>
        <input type={show ? 'text' : 'password'} placeholder="••••••••••••" defaultValue={defaultValue} className="w-full pl-11 pr-12 py-3 bg-[#F8FAFC] border border-slate-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all" data-id="element-3667" />
        <button onClick={onToggle} className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600" data-id="element-3668">
          {show ? <EyeOff size={18} data-id="element-3669" /> : <Eye size={18} data-id="element-3670" />}
        </button>
      </div>
    </div>;
}
function Step({
  step,
  title,
  desc,
  active
}: any) {
  return <div className="flex items-center gap-4" data-id="element-3671">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${active ? 'bg-[#10B981] text-white' : 'bg-white/10 text-slate-400'}`} data-id="element-3672">
        {step}
      </div>
      <div data-id="element-3673">
        <p className="text-sm font-bold" data-id="element-3674">{title}</p>
        <p className="text-[10px] text-slate-500 font-medium" data-id="element-3675">{desc}</p>
      </div>
    </div>;
}